import { Box, Button, DialogActions, DialogContent, Divider, IconButton, ModalDialog, Stack, Typography } from '@mui/joy';
import { FC, FormEvent } from 'react';
import { GiEmptyHourglass } from 'react-icons/gi';
import { TfiClose } from 'react-icons/tfi';
import { useShallow } from 'zustand/react/shallow';

import { EntitlementServiceType, useFetchEntitlements } from '@MP/shared/api/hooks/contract';
import { useUpdatePatient } from '@MP/shared/api/hooks/patient';
import { RectangleButton } from '@MP/shared/components/Button';
import { useMinddSnackbar } from '@MP/shared/components/MinddSnackbarProvider';
import Section from '@MP/shared/components/Section';
import MemberDeleteIcon from '@MP/shared/icon/MemberDeleteIcon';
import { usePatientStore } from '@MP/shared/providers/PatientProvider';
import { isStimHomeEntitled } from '@MP/shared/utils/StimHomeUtils';

import { usePatientEditFormStore, usePatientEditFormStoreApi } from '../../providers/PatientEditFormProvider';
import {
  selectAgreedTypes,
  selectIsPatientRegistrationAgreed,
  selectIsPatientRegistrationExpired,
  selectIsStimExpired,
  selectIsStimHomeAgreed,
  selectIsStimHomeExpired,
} from '../../stores/createPatientEditFormDataStore';
import PatientEditForm from '../PatientEditForm';

const ExpiredBadge = () => (
  <Typography
    level="body-xs"
    sx={{
      color: '#E5484D',
      fontWeight: 600,
      bgcolor: '#FDECEC',
      borderRadius: '4px',
      px: '6px',
      py: '2px',
    }}>
    만료
  </Typography>
);

export const PatientEditModalDialog: FC<{
  onClose: () => void;
  onNext: () => void;
  onDelete: () => void;
}> = ({ onNext, onClose, onDelete }) => {
  const formDataApi = usePatientEditFormStoreApi();
  const updateUser = usePatientStore((s) => s.updateUser);
  const snackbar = useMinddSnackbar();
  const { mutateAsync: updatePatient, isPending: isUpdating } = useUpdatePatient();
  const { data: entitlements } = useFetchEntitlements(EntitlementServiceType.StimHome);

  const isPatientRegistrationAgreed = usePatientEditFormStore(selectIsPatientRegistrationAgreed);
  const isPatientRegistrationExpired = usePatientEditFormStore(selectIsPatientRegistrationExpired);
  const isStimHomeAgreed = usePatientEditFormStore(selectIsStimHomeAgreed);
  const isStimHomeExpired = usePatientEditFormStore(selectIsStimHomeExpired);
  const isStimExpired = usePatientEditFormStore(selectIsStimExpired);
  const agreedTypes = usePatientEditFormStore(useShallow(selectAgreedTypes));

  // 동의서 전송 다이얼로그에서 실제로 노출될 항목이 하나라도 있는지
  const showPatientRegistration = !isPatientRegistrationAgreed || isPatientRegistrationExpired;
  const showStimHome = isStimHomeEntitled(entitlements) && (!isStimHomeAgreed || isStimHomeExpired);

  const handleUpdate = async (e: FormEvent) => {
    e.preventDefault();
    const { id, code, name, handedness, gender, phoneNumber, birth } = formDataApi.getState();
    if (id) {
      const res = await updatePatient({
        patientId: id,
        body: {
          code,
          name,
          handedness: handedness || 'NONE',
          gender,
          phoneNumber,
          birthYear: parseInt(birth.substring(0, 4), 10),
          birthMonth: parseInt(birth.substring(4, 6), 10),
          birthDay: parseInt(birth.substring(6, 8), 10),
        },
      });
      snackbar.add('환자 정보가 수정되었습니다.');
      updateUser(res, true);
    }
    onClose();
  };

  const handlePopup = () => {
    const { name, agreed } = formDataApi.getState();
    const stim = agreed.find((v) => v.serviceType === 'STIM');

    window.open(
      `/print/stim?userName=${name}&date=${stim?.createdAt}`,
      'AgreementView',
      `width=700, height=900, status=no, menubar=no, toolbar=no, resizable=no`
    );
  };

  return (
    <ModalDialog sx={{ gap: '16px' }} component={'form'} onSubmit={handleUpdate}>
      <Stack direction={'row'} justifyContent={'space-between'}>
        <Typography level="h4" fontWeight={600}>
          환자 정보
        </Typography>
        <IconButton onClick={onClose}>
          <TfiClose fontSize={20} />
        </IconButton>
      </Stack>
      <DialogContent sx={{ gap: '16px' }}>
        <PatientEditForm />
        <Box>
          <Typography sx={{ fontSize: 13, color: '#666', fontWeight: 500 }}>동의한 항목</Typography>
          <Section sx={{ p: '16px', mt: '8px' }}>
            <Stack gap={'16px'} divider={<Divider />}>
              {isPatientRegistrationAgreed && (
                <Stack direction={'row'} alignItems={'center'} spacing={'8px'}>
                  <Typography level="body-sm" sx={{ color: '#666' }}>
                    MINDD 서비스 이용 동의
                  </Typography>
                  {isPatientRegistrationExpired && <ExpiredBadge />}
                </Stack>
              )}
              {agreedTypes.includes('STIM') && (
                <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                  <Stack direction={'row'} alignItems={'center'} spacing={'8px'}>
                    <Typography level="body-sm" sx={{ color: '#666' }}>
                      MINDD STIM+ 비급여 동의
                    </Typography>
                    {isStimExpired && <ExpiredBadge />}
                  </Stack>
                  <Button variant="plain" onClick={handlePopup}>
                    동의서 출력
                  </Button>
                </Stack>
              )}
              {isStimHomeAgreed && (
                <Stack direction={'row'} alignItems={'center'} spacing={'8px'}>
                  <Typography level="body-sm" sx={{ color: '#666' }}>
                    STIM HOME 서비스 이용 동의
                  </Typography>
                  {isStimHomeExpired && <ExpiredBadge />}
                </Stack>
              )}
              {!isPatientRegistrationAgreed && !agreedTypes.includes('STIM') && !isStimHomeAgreed && (
                <Stack alignItems={'center'} spacing={'4px'}>
                  <GiEmptyHourglass fontSize={20} />
                  <Typography level="body-sm" sx={{ color: '#666' }}>
                    동의한 항목이 없습니다.
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Section>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ pt: 0 }}>
        <RectangleButton type="submit" loading={isUpdating}>
          저장
        </RectangleButton>
        {(showPatientRegistration || showStimHome) && (
          <RectangleButton variant="outlined" onClick={onNext}>
            동의서 전송
          </RectangleButton>
        )}
        <Box flexGrow={1}>
          <RectangleButton
            disabled={isUpdating}
            variant="plain"
            sx={{ height: 52 }}
            startDecorator={<MemberDeleteIcon />}
            onClick={onDelete}>
            <Typography color="danger" sx={{ fontSize: 16, fontWeight: 400 }}>
              환자 삭제
            </Typography>
          </RectangleButton>
        </Box>
      </DialogActions>
    </ModalDialog>
  );
};
